export interface ReportNotes {
  name: string;
  pronouns: 'he/him' | 'she/her' | 'they/them';
  notes: string;
  selectedComments: string[];
}

// Student attributes data
export const STUDENT_ATTRIBUTES = {
  personal: [
    'thoughtful', 'kind', 'respectful', 'caring', 'considerate',
    'polite', 'friendly', 'honest', 'cheerful', 'helpful',
    'enthusiastic', 'fun-loving', 'valued', 'calm', 'energetic'
  ],
  academic: [
    'diligent', 'focused', 'hardworking', 'enthusiastic', 'motivated',
    'curious', 'attentive', 'independent', 'organised', 'responsible',
    'inquisitive'
  ],
  social: [
    'cooperative', 'inclusive', 'supportive', 'empathetic', 'adaptable',
    'patient', 'mature', 'engaged', 'well-mannered', 'reliable',
    'enjoys playing with friends'
  ]
};

// Adjusting to school expectations options
export const ADJUSTING_TO_SCHOOL_OPTIONS = [
  'Adjusting very well to classroom and school expectations',
  'Adjusting well, with occasional reminders or support',
  'Experiencing some difficulty adjusting; requires regular guidance',
  'Struggling to adjust; needs ongoing support and intervention'
];

// Comment categories with rating levels and specific comments
export interface CommentCategory {
  id: string;
  title: string;
  ratingLevels: {
    [key: string]: string[];
  };
}

export const COMMENT_CATEGORIES: { [key: string]: CommentCategory } = {
  friendships: {
    id: 'friendships',
    title: 'Friendships',
    ratingLevels: {
      'highly-positive': [
        'Builds and maintains positive and respectful relationships with peers and staff.',
        'Is a caring and inclusive class member who values others\' contributions.',
        'Works cooperatively with a range of peers and supports others in group settings.',
        'Demonstrates empathy and kindness in interactions with classmates.',
        'Is a trusted and reliable friend who treats others with fairness and respect.',
        'Helps to foster a warm and inclusive classroom environment.'
      ],
      'generally-positive': [
        'Usually interacts positively with peers and is developing strong friendships.',
        'Shows a willingness to work with different classmates and form new connections.',
        'Is developing confidence in social situations and contributes to a positive class dynamic.',
        'Is kind and considerate, often offering help and encouragement to others.',
        'Demonstrates growing empathy and awareness of others\' feelings.',
        'Enjoys working collaboratively and participates respectfully in group tasks.'
      ],
      'needs-support': [
        'Is learning to build and maintain positive relationships with peers.',
        'Is building the skills to show greater empathy and cooperation in group settings.',
        'Is developing the skills to resolve conflicts respectfully and independently.',
        'Is working on including others and sharing more in social situations.',
        'Benefits from guidance in forming and maintaining friendships.',
        'Is encouraged to interact more positively and respectfully with a wider range of peers.'
      ]
    }
  },
  teamwork: {
    id: 'teamwork',
    title: 'Teamwork',
    ratingLevels: {
      'highly-positive': [
        'Works exceptionally well with others and contributes positively to group tasks.',
        'Demonstrates leadership and initiative when working in teams.',
        'Listens respectfully to others and values different perspectives.',
        'Collaborates effectively to achieve shared goals and supports peers willingly.',
        'Contributes ideas confidently and encourages others to do the same.',
        'Helps create a cooperative and inclusive team environment.'
      ],
      'generally-positive': [
        'Works well with peers and is developing confidence to share ideas in group settings.',
        'Contributes ideas during group work and listens to others respectfully.',
        'Is learning to share tasks fairly and support team goals.',
        'Is building confidence to take on more active roles in group discussions.',
        'Participates positively in structured group activities with guidance.',
        'Is beginning to show initiative in group tasks and joint problem-solving.'
      ],
      'needs-support': [
        'Is learning to work more cooperatively with a range of peers.',
        'Needs reminders to contribute constructively to group discussions.',
        'Is developing the ability to compromise and work towards shared outcomes.',
        'Requires support to stay focused and engaged in group settings.',
        'Is encouraged to take a more active and respectful role in teamwork.',
        'Is learning to manage disagreements respectfully and work through challenges collaboratively.'
      ]
    }
  },
  learningBehaviours: {
    id: 'learningBehaviours',
    title: 'Learning Behaviours',
    ratingLevels: {
      'highly-positive': [
        'Consistently demonstrates a proactive and responsible approach to learning.',
        'Is self-motivated and takes initiative in all learning tasks.',
        'Listens attentively and responds thoughtfully during class discussions.',
        'Stays focused and completes tasks with care and attention to detail.',
        'Takes pride in work and strives to achieve their personal best.',
        'Works well independently and collaborates effectively with peers.'
      ],
      'generally-positive': [
        'Demonstrates a positive attitude towards learning and usually attempts learning tasks independently.',
        'Is developing greater independence and takes increasing responsibility for learning.',
        'Usually listens carefully and follows instructions with minimal prompting.',
        'Listens to class conversations and is beginning to contribute thoughts and ideas.',
        'Is building confidence in managing time and staying on task.',
        'Is building confidence to seek out feedback to support continued growth.'
      ],
      'needs-support': [
        'Is learning to stay focused and complete tasks within set timeframes.',
        'Requires regular prompting to remain on task and complete activities.',
        'Is developing the ability to take responsibility for their learning.',
        'Is encouraged to participate more actively in class discussions.',
        'Is learning to use feedback to improve the quality of work.',
        'Requires support to attempt learning tasks with a positive mindset.'
      ]
    }
  },
  growthMindset: {
    id: 'growthMindset',
    title: 'Growth Mindset',
    ratingLevels: {
      'highly-positive': [
        'Demonstrates resilience and approaches challenges with a positive attitude.',
        'Embraces feedback as a tool for learning and improvement.',
        'Is developing confidence to try new strategies when faced with difficulty.',
        'Understands that effort leads to improvement and values the learning process.',
        'Is willing to take academic risks and learn from mistakes.',
        'Displays persistence and continues to work hard, even when tasks are challenging.'
      ],
      'generally-positive': [
        'Is learning to persevere through challenges and remain focused on goals.',
        'Is beginning to view mistakes as opportunities to learn.',
        'Benefits from encouragement to stay positive when tasks are difficult.',
        'Is working on applying teacher feedback to improve outcomes.',
        'Is developing confidence to attempt unfamiliar or complex tasks.'
      ],
      'needs-support': [
        'Is learning to accept feedback positively and use it to improve.',
        'Needs support to build resilience when faced with challenging tasks.',
        'Is encouraged to shift focus from perfection to progress and learning.',
        'Is developing the ability to reflect on learning and identify next steps.',
        'Requires guidance to approach tasks with a more optimistic mindset.'
      ]
    }
  },
  goals: {
    id: 'goals',
    title: 'Goals',
    ratingLevels: {
      'highly-positive': [
        'to take on greater leadership roles within the classroom and broader school community.',
        'to mentor and support peers through collaborative tasks and group projects.',
        'to seek opportunities to extend learning through self-directed inquiry and independent research.',
        'to take initiative in proposing new ideas or activities that enhance the classroom environment.',
        'to demonstrate leadership by modelling respectful and responsible behaviour.',
        'to set personal academic goals that challenge current levels of achievement.',
        'to contribute more actively to whole-school initiatives or student voice opportunities.',
        'to reflect critically on learning habits to enhance independence and effectiveness even further.'
      ],
      'generally-positive': [
        'to remain focused and complete tasks within set timeframes.',
        'to take greater responsibility for independent learning tasks.',
        'to actively participate in class discussions and group work.',
        'to apply teacher feedback more consistently to improve outcomes.',
        'to approach new and challenging tasks with confidence and positivity.',
        'to improve organisational skills and manage time effectively.',
        'to develop greater resilience when faced with setbacks or errors.',
        'to stay on task without frequent reminders or redirection.'
      ],
      'needs-support': [
        'to manage time and personal belongings more effectively.',
        'to begin planning and prioritising tasks with teacher guidance.',
        'to reduce reliance on adult support by using visual or verbal prompts.',
        'to work towards completing set tasks within given timeframes.',
        'to break tasks into manageable steps and work through them sequentially.',
        'to respond calmly to feedback and redirection.',
        'to use strategies to regulate emotions and remain in the learning space.',
        'to interact respectfully with peers and adults.',
        'to recognise and express needs in appropriate ways.',
        'to begin forming positive relationships with classmates and staff.',
        'to follow school rules and expectations with adult support.'
      ]
    }
  }
};

// Rating level display names
export const RATING_LEVEL_LABELS = {
  'highly-positive': 'Highly positive',
  'generally-positive': 'Generally positive',
  'needs-support': 'Needs support'
};

// Form data interfaces
export interface StudentAttributes {
  personal: string[];
  academic: string[];
  social: string[];
}

export interface StudentAttributesWithOther {
  personal: string[];
  academic: string[];
  social: string[];
  personalOther?: string;
  academicOther?: string;
  socialOther?: string;
}

export interface CategorySelection {
  ratingLevel?: string;
  selectedComments: string[];
  otherComment?: string;
}

export interface StudentReportData {
  name: string;
  pronouns?: 'he/him' | 'she/her' | 'they/them';
  attributes: StudentAttributesWithOther;
  adjustingToSchool?: string;
  categories: {
    [categoryId: string]: CategorySelection;
  };
  otherNotes?: string;
}

export interface GeneralCommentsFormData {
  students: StudentReportData[];
  characterCountRange?: string;
  commentStyle?: 'bullet' | 'paragraph';
  customInstructions?: string;
}

export interface MathematicsCommentsFormData {
  commentStyle: 'bullet' | 'paragraph';
  characterLimit?: string;
  customInstructions?: string;
  demonstratedContent: string;
  strength1: string;
  strength2: string;
  goal1: string;
  goal2: string;
  studentName?: string;
  studentEngagement?: 'Highly positive' | 'Generally positive' | 'Needs support';
  studentAchievement?: 'Working above grade expectations' | 'Working at grade expectations' | 'Working toward grade expectations';
}

export interface EnglishCommentsFormData {
  commentStyle: 'bullet' | 'paragraph';
  characterLimit?: string;
  customInstructions?: string;
  demonstratedContent: string;
  strength1: string;
  strength2: string;
  goal1: string;
  goal2: string;
  studentName?: string;
  studentEngagement?: 'Highly positive' | 'Generally positive' | 'Needs support';
  studentAchievement?: 'Working above grade expectations' | 'Working at grade expectations' | 'Working toward grade expectations';
}
